import { useMemo } from 'react'

import _ from 'lodash'
import { DateTime } from 'luxon'
import { toast } from 'sonner'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecords } from '@/shared/hooks'
import { getErrorMessage, showErrorAlert } from '@/shared/lib/errors'
import { api } from '@/shared/lib/api'
import errorMessages from '@/shared/lib/errors/translations/api.json'

import {
  isStoreClosedError,
  showStoreClosedAlert
} from './lib/storeClosedNotice'
import { useRewardsStore } from '@/shared/store/useRewardsStore'
import { useResourceContext } from '@/shared/ui/Resource'

import { hasReachedMaxPerUser } from './lib/allowance'
import { resolveStoreHours } from './lib/storeHours'

const TRANSACTIONS = {
  withdraw: {
    getPath: digitalProductId =>
      `/user/digital_products/${digitalProductId}/reward_requests/deliver`,
    messages: {
      success: {
        title: 'Retirada realizada',
        text: 'A retirada deste item foi registrada com sucesso'
      },
      error: { title: 'Não foi possível registrar a retirada deste item' }
    }
  },
  refund: {
    getPath: (digitalProductId, id) =>
      `/user/digital_products/${digitalProductId}/reward_requests/${id}/cancel`,
    messages: {
      success: {
        title: 'Reembolso realizado',
        text: 'O reembolso deste item foi registrado com sucesso'
      },
      error: { title: 'Não foi possível registrar o reembolso deste item' }
    }
  }
}

export const useDigitalProductStore = rewardId => {
  const {
    digitalProductId,
    digitalProduct,
    progress,
    refetch: refetchStoreStatus
  } = useDigitalProductContext()
  const storeHourWindows = digitalProduct?.store_setting?.store_hours
  const storeHours = useMemo(
    () => resolveStoreHours({ windows: storeHourWindows }),
    [storeHourWindows]
  )
  const {
    openAt: storeOpenAt,
    closeAt: storeCloseAt,
    status: storeStatus
  } = storeHours

  const { reward, rewardRequests: allRewardRequests } = useRewardsStore({
    id: rewardId
  })

  const rewardRequests = allRewardRequests.filter(
    request => request.reward_id === rewardId
  )
  const maxRequestsPerUser = reward?.max_quantity_per_user
  // Só consomem a cota resgates `delivered` ou `approved` não expirados — um
  // resgate que expirou sem retirada libera o item novamente. Ver lib/allowance.
  const hasRequestedMaxQuantityPerUser = useMemo(
    () => hasReachedMaxPerUser(rewardRequests, maxRequestsPerUser),
    [rewardRequests, maxRequestsPerUser]
  )

  const getStock = (quantity, quantity_sold) => {
    const stock = quantity - quantity_sold
    return _.isNaN(stock) ? 0 : stock
  }

  const isOpen = () =>
    storeStatus === 'open' || storeStatus === 'unscheduled'

  const willOpenSoon = () => storeStatus === 'soon'

  const alreadyClosed = () => storeStatus === 'closed'

  // `closeAt` é o fechamento da janela corrente — é o prazo que a vitrine
  // promete ao Participante e o mesmo que a api grava em `expires_at`.
  const openingHours = {
    openAt: storeOpenAt,
    closeAt: storeCloseAt
  }

  const isAvailable = rewardItem => {
    return (
      !hasRequestedMaxQuantityPerUser &&
      getStock(rewardItem.quantity, rewardItem.quantity_sold) > 0
    )
  }

  const polling = async ({
    id,
    maxAttempts = 100,
    interval = 2000,
    onSuccess
  }) => {
    let attempts = 0
    const poll = setInterval(async () => {
      try {
        const rewardRequest = await api
          .get(
            `/user/digital_products/${digitalProductId}/reward_requests/${id}`
          )
          .then(response => response.data.record)
        if (rewardRequest.status !== 'draft') {
          onSuccess && onSuccess(rewardRequest)
          clearInterval(poll)
        } else if (++attempts >= maxAttempts) {
          clearInterval(poll)
        }
      } catch (error) {
        clearInterval(poll)
      }
    }, interval)
  }

  const claim = async ({ reward_id, reward_item_id, quantity }) => {
    try {
      const {
        data: { record: rewardRequest, errors }
      } = await api.post(
        `/user/digital_products/${digitalProductId}/reward_requests`,
        {
          record: { reward_id, reward_item_id, quantity }
        }
      )

      return { rewardRequest }
    } catch (error) {
      /* A lojinha fechada não é "não foi possível": é uma regra, e tem hora
         para deixar de valer. O toast genérico dizia o contrário e não dizia
         quando voltar. */
      if (isStoreClosedError(error)) {
        void showStoreClosedAlert(openingHours?.openAt)
        return { record: null, error: null }
      }

      const errorMessage = getErrorMessage(error, errorMessages)
      toast.error('Não foi possível resgatar este item', {
        description: errorMessage.message
      })
      return { record: null, error: errorMessage }
    }
  }

  const transaction = async ({ ids, kind }) => {
    if (!_.includes(Object.keys(TRANSACTIONS), kind)) return

    if (kind === 'refund' && ids.length > 1) return

    try {
      const {
        data: { records: rewardRequests, errors }
      } = await api.post(
        `${TRANSACTIONS[kind].getPath(digitalProductId, ids[0])}`,
        { record: { ids } }
      )

      toast.success(TRANSACTIONS[kind].messages.success.title, {
        description: TRANSACTIONS[kind].messages.success.text
      })

      return { rewardRequests }
    } catch (error) {
      const errorMessage = getErrorMessage(error, errorMessages)
      toast.error(TRANSACTIONS[kind].messages.error.title, {
        description: errorMessage.message
      })
      return { record: null, error: errorMessage }
    }
  }

  const withdraw = async ids => {
    try {
      const {
        data: { records: rewardRequests, errors }
      } = await api.post(
        `/user/digital_products/${digitalProductId}/reward_requests/deliver`,
        { record: { ids } }
      )

      toast.success('Retirada realizada', {
        description: 'A retirada deste item foi registrada com sucesso'
      })

      return { rewardRequests }
    } catch (error) {
      const errorMessage = getErrorMessage(error, errorMessages)
      toast.error('Não foi possível registrar a retirada deste item', {
        description: errorMessage.message
      })
      return { record: null, error: errorMessage }
    }
  }

  return {
    claim,
    polling,
    rewardRequests,
    hasRequestedMaxQuantityPerUser,
    getStock,
    isOpen,
    willOpenSoon,
    alreadyClosed,
    storeStatus,
    refetchStoreStatus,
    openingHours,
    isAvailable,
    withdraw,
    transaction
  }
}
